import type { ApiClient } from './client';

export type ExportFormat = 'csv' | 'json';
export type ExportStatus = 'pending' | 'processing' | 'ready' | 'failed' | 'expired';

export interface ConversationExport {
  id: string;
  format: ExportFormat;
  status: ExportStatus;
  params: unknown;
  rowsCount: number | null;
  fileSize: number | null;
  errorMessage: string | null;
  downloadUrl: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
}

interface RawExport {
  id: string;
  format: ExportFormat;
  status: ExportStatus;
  params: unknown;
  rows_count?: number | null;
  file_size?: number | null;
  error_message?: string | null;
  download_url?: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  expires_at?: string | null;
}

function toExport(r: RawExport): ConversationExport {
  return {
    id: r.id,
    format: r.format,
    status: r.status,
    params: r.params,
    rowsCount: r.rows_count ?? null,
    fileSize: r.file_size ?? null,
    errorMessage: r.error_message ?? null,
    downloadUrl: r.download_url ?? null,
    createdAt: r.created_at,
    startedAt: r.started_at ?? null,
    completedAt: r.completed_at ?? null,
    expiresAt: r.expires_at ?? null,
  };
}

export async function listExports(client: ApiClient): Promise<ConversationExport[]> {
  const raw = await client.request<{ exports: RawExport[] }>('/api/v1/exports');
  return raw.exports.map(toExport);
}

export async function getExport(client: ApiClient, id: string): Promise<ConversationExport> {
  const raw = await client.request<{ export: RawExport }>(`/api/v1/exports/${id}`);
  return toExport(raw.export);
}

export interface CreateExportInput {
  format: ExportFormat;
  /** RFC3339 или YYYY-MM-DD. */
  from: string;
  /** RFC3339 или YYYY-MM-DD. */
  to: string;
  siteId?: string | null;
  conversationId?: string | null;
}

export async function createExport(
  client: ApiClient,
  input: CreateExportInput,
): Promise<ConversationExport> {
  const raw = await client.request<{ export: RawExport }>('/api/v1/exports', {
    method: 'POST',
    json: {
      format: input.format,
      from: input.from,
      to: input.to,
      site_id: input.siteId ?? undefined,
      conversation_id: input.conversationId ?? undefined,
    },
  });
  return toExport(raw.export);
}
